import { useState } from 'react';
import type { ApiMetaobject } from '@dunea/plugin-sdk';
import { AdminExtensionSlot } from '~/components/workbench/admin-extensions';
import { FieldsForm, draftErrors, pruneEmpty, type ReferenceLoader } from '~/components/workbench/fields';
import { withFieldDefaults } from '~/components/workbench/fields/sdk';
import {
  apiCreateMetaobject,
  apiDeleteMetaobject,
  apiListMetaobjects,
  apiReorderMetaobjects,
  apiUpdateMetaobject,
  type DataDefinition,
} from '~/lib/api/data';
import { classNames } from '~/utils/classNames';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CARD,
  ErrorBanner,
  LoadError,
  Pill,
  Section,
  Segmented,
  SkeletonCard,
  Spinner,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import {
  PAGE_SIZE,
  apiErrorMessage,
  canEditDefinition,
  canEditValues,
  entryTitle,
  moveItem,
  namespaceLabel,
} from './content-model';
import { qualifyReference } from './references';

/** Server field errors (studio answers `{ errors: { key: message } }` on 400). */
function serverErrors(err: unknown): Record<string, string> | undefined {
  const errors = (err as { response?: { data?: { errors?: unknown } } } | null)?.response?.data?.errors;
  return errors && typeof errors === 'object' ? (errors as Record<string, string>) : undefined;
}

export function EntriesView({
  appId,
  def,
  references,
  onBack,
  onEditType,
  onCountChange,
}: {
  appId: string;
  def: DataDefinition;
  references: ReferenceLoader;
  onBack: () => void;
  onEditType: () => void;
  onCountChange: (n: number) => void;
}) {
  const entries = useResource(
    () => apiListMetaobjects(appId, def.key, { namespace: def.namespace, limit: PAGE_SIZE }),
    `${appId}:${def.id}`,
  );
  const [editing, setEditing] = useState<ApiMetaobject | 'new' | null>(null);
  const [reorderError, setReorderError] = useState<string | null>(null);
  const editable = canEditValues(def);
  const items = entries.data?.items ?? [];
  const from = namespaceLabel(def.namespace);

  const setItems = (next: ApiMetaobject[]) => {
    entries.setData((page) => ({
      ...(page ?? { type: def.key, namespace: def.namespace, page: 1, pageSize: PAGE_SIZE, hasMore: false }),
      items: next,
    }));
    onCountChange(next.length);
  };

  const move = async (from: number, to: number) => {
    const next = moveItem(items, from, to);
    const before = items;

    setItems(next);
    setReorderError(null);

    try {
      await apiReorderMetaobjects(appId, { type: def.key, namespace: def.namespace, ids: next.map((e) => e.id) });
    } catch (err) {
      setItems(before);
      setReorderError(apiErrorMessage(err, 'Could not save the new order.'));
    }
  };

  if (editing) {
    return (
      <EntryEditor
        appId={appId}
        def={def}
        entry={editing === 'new' ? null : editing}
        references={references}
        onClose={() => setEditing(null)}
        onSaved={(saved) => {
          const exists = items.some((e) => e.id === saved.id);
          setItems(exists ? items.map((e) => (e.id === saved.id ? saved : e)) : [...items, saved]);
          setEditing(null);
        }}
        onDeleted={(id) => {
          setItems(items.filter((e) => e.id !== id));
          setEditing(null);
        }}
      />
    );
  }

  return (
    <div className="flex-1 space-y-5 overflow-y-auto p-4">
      <div className="flex items-center justify-between gap-2">
        <button type="button" className={classNames(BTN_GHOST, '-ml-2.5')} onClick={onBack}>
          <div className="i-ph:arrow-left text-[12px]" />
          Content types
        </button>
        {canEditDefinition(def) && (
          <button type="button" className={BTN_GHOST} onClick={onEditType}>
            <div className="i-ph:sliders-horizontal text-[12px]" />
            Edit type
          </button>
        )}
      </div>

      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <h4 className="text-[15px] font-medium tracking-tight text-primary">{def.name}</h4>
          {def.access.storefront !== 'public' && <Pill tone="muted" label="Private" />}
          {from && <Pill tone="info" label={from} />}
        </div>
        <p className="text-[11px] leading-relaxed text-muted">
          {def.description ||
            (def.access.storefront === 'public'
              ? 'Active entries show on your site in this order.'
              : 'Private: your site cannot read this type.')}
        </p>
      </div>

      <Section
        title="Entries"
        icon="i-ph:list-bullets"
        aside={
          editable && (
            <button type="button" className={BTN_SECONDARY} onClick={() => setEditing('new')}>
              <div className="i-ph:plus text-[13px]" />
              New entry
            </button>
          )
        }
      >
        {reorderError && <ErrorBanner message={reorderError} />}
        {entries.loading && !entries.data ? (
          <>
            <SkeletonCard lines={1} />
            <SkeletonCard lines={1} />
          </>
        ) : entries.error ? (
          <LoadError message="Could not load entries." onRetry={() => void entries.reload()} />
        ) : !items.length ? (
          <div className={classNames(CARD, 'space-y-1.5 px-4 py-6 text-center')}>
            <div className="i-ph:list-bullets mx-auto text-xl text-faint" />
            <p className="text-[12px] leading-relaxed text-muted">No entries yet.</p>
          </div>
        ) : (
          <ol className={classNames(CARD, 'divide-y divide-hairline')}>
            {items.map((entry, i) => (
              <li key={entry.id} className="flex items-center gap-2 px-3 py-2">
                <button
                  type="button"
                  className="min-w-0 flex-1 rounded-md px-1 py-1 text-left transition-colors hover:bg-white/[0.03]"
                  onClick={() => setEditing(entry)}
                >
                  <div className="truncate text-[13px] text-primary">{entryTitle(entry, def)}</div>
                  <div className="truncate font-geist-mono text-[10px] text-muted">{entry.handle}</div>
                </button>
                {entry.status === 'draft' && <Pill tone="warning" label="Draft" />}
                {editable && items.length > 1 && (
                  <div className="flex shrink-0 items-center">
                    <button
                      type="button"
                      className="ui-btn-icon"
                      aria-label={`Move ${entryTitle(entry, def)} up`}
                      disabled={i === 0}
                      onClick={() => void move(i, i - 1)}
                    >
                      <span className="i-ph:caret-up text-[12px] text-muted" />
                    </button>
                    <button
                      type="button"
                      className="ui-btn-icon"
                      aria-label={`Move ${entryTitle(entry, def)} down`}
                      disabled={i === items.length - 1}
                      onClick={() => void move(i, i + 1)}
                    >
                      <span className="i-ph:caret-down text-[12px] text-muted" />
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ol>
        )}
        {entries.data?.hasMore && <p className="text-center text-[11px] text-muted">Showing the first {PAGE_SIZE}.</p>}
      </Section>
    </div>
  );
}

function EntryEditor({
  appId,
  def,
  entry,
  references,
  onClose,
  onSaved,
  onDeleted,
}: {
  appId: string;
  def: DataDefinition;
  entry: ApiMetaobject | null;
  references: ReferenceLoader;
  onClose: () => void;
  onSaved: (saved: ApiMetaobject) => void;
  onDeleted: (id: string) => void;
}) {
  const fields = (def.fields ?? []).map((f) => qualifyReference(f, def.namespace));
  const editable = canEditValues(def);
  const [values, setValues] = useState<Record<string, unknown>>(() =>
    entry ? { ...entry.fields } : withFieldDefaults(fields, {}),
  );
  const [status, setStatus] = useState<'draft' | 'active'>(entry?.status ?? 'active');
  const [handle, setHandle] = useState(entry?.handle ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'save' | 'delete' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();

    const problems = draftErrors(fields, values);
    setErrors(problems);
    setError(null);

    if (Object.keys(problems).length) {
      return;
    }

    setBusy('save');

    try {
      const body = pruneEmpty(values);
      const saved = entry
        ? await apiUpdateMetaobject(appId, entry.id, {
            // Cleared fields are sent as null so studio removes them.
            fields: { ...Object.fromEntries(Object.keys(entry.fields).map((k) => [k, null])), ...body },
            status,
            ...(handle.trim() && handle.trim() !== entry.handle ? { handle: handle.trim() } : {}),
          })
        : await apiCreateMetaobject(appId, {
            type: def.key,
            namespace: def.namespace,
            fields: body,
            status,
            ...(handle.trim() ? { handle: handle.trim() } : {}),
          });

      onSaved(saved);
    } catch (err) {
      setErrors(serverErrors(err) ?? {});
      setError(apiErrorMessage(err, 'Could not save the entry.'));
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!entry) {
      return;
    }

    setBusy('delete');
    setError(null);

    try {
      await apiDeleteMetaobject(appId, entry.id);
      onDeleted(entry.id);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not delete the entry.'));
      setBusy(null);
    }
  };

  return (
    <form className="flex-1 space-y-5 overflow-y-auto p-4" onSubmit={(e) => void save(e)}>
      <button type="button" className={classNames(BTN_GHOST, '-ml-2.5')} onClick={onClose}>
        <div className="i-ph:arrow-left text-[12px]" />
        {def.name}
      </button>

      <h4 className="text-[15px] font-medium tracking-tight text-primary">
        {entry ? entryTitle({ fields: values, handle: entry.handle }, def) : `New ${def.name.toLowerCase()}`}
      </h4>

      <div className={classNames(CARD, 'p-4')}>
        <FieldsForm
          fields={fields}
          values={values}
          onChange={setValues}
          errors={errors}
          disabled={!editable || busy !== null}
          references={references}
        />
      </div>

      <div className={classNames(CARD, 'space-y-3 p-4')}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-[11px] text-secondary">Status</span>
          <Segmented
            label="Status"
            value={status}
            options={[
              ['active', 'Active'],
              ['draft', 'Draft'],
            ]}
            onChange={setStatus}
          />
        </div>
        <label className="block space-y-1.5">
          <span className="block text-[11px] text-secondary">Handle</span>
          <input
            className="ui-input-mono"
            value={handle}
            placeholder="Generated from the title"
            disabled={!editable || busy !== null}
            onChange={(e) => setHandle(e.target.value)}
          />
          <span className="block text-[11px] leading-relaxed text-muted">
            Used in links, e.g. /{def.key.replace(/_/g, '-')}/{handle || 'jane-doe'}. Drafts never show on the site.
          </span>
        </label>
      </div>

      {/* Plugin cards for this entry (data.metaobject.details); saved entries only. */}
      {entry && <AdminExtensionSlot appId={appId} target="data.metaobject.details" resourceId={entry.id} />}

      {error && <ErrorBanner message={error} />}

      {editable ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          {entry ? (
            confirmDelete ? (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  className={classNames(BTN_SECONDARY, '!text-danger')}
                  disabled={busy !== null}
                  onClick={() => void remove()}
                >
                  {busy === 'delete' ? <Spinner /> : <div className="i-ph:trash text-[13px]" />}
                  Delete entry
                </button>
                <button type="button" className={BTN_GHOST} onClick={() => setConfirmDelete(false)}>
                  Keep
                </button>
              </div>
            ) : (
              <button type="button" className={BTN_GHOST} onClick={() => setConfirmDelete(true)}>
                <div className="i-ph:trash text-[13px]" />
                Delete
              </button>
            )
          ) : (
            <span />
          )}
          <div className="flex items-center gap-1.5">
            <button type="button" className={BTN_GHOST} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="ui-btn-accent !px-4 !py-2 !text-[12px]" disabled={busy !== null}>
              {busy === 'save' && <Spinner />}
              {entry ? 'Save' : 'Add entry'}
            </button>
          </div>
        </div>
      ) : (
        <p className="text-[11px] leading-relaxed text-muted">
          This plugin manages these entries; you can view them here.
        </p>
      )}
    </form>
  );
}
